export interface EncryptedBlob {
  ciphertext: ArrayBuffer;
  iv: Uint8Array;
  salt: Uint8Array;
}

// Normalize to a fresh ArrayBuffer-backed view — avoids the
// ArrayBuffer vs SharedArrayBuffer type conflict in strict TS.
function toAB(u8: Uint8Array): Uint8Array {
  const copy = new Uint8Array(u8.length);
  copy.set(u8);
  return copy;
}

async function deriveKey(
  passphrase: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const baseKey = await crypto.subtle.importKey(
    'raw',
    toAB(enc.encode(passphrase)) as BufferSource,
    'PBKDF2',
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: toAB(salt) as BufferSource,
      iterations: 600_000,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function encryptString(
  plaintext: string,
  passphrase: string,
): Promise<EncryptedBlob> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toAB(iv) as BufferSource },
    key,
    toAB(new TextEncoder().encode(plaintext)) as BufferSource,
  );
  return { ciphertext, iv, salt };
}

export async function decryptString(
  blob: EncryptedBlob,
  passphrase: string,
): Promise<string> {
  const key = await deriveKey(passphrase, blob.salt);
  const plaintext = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toAB(blob.iv) as BufferSource },
    key,
    blob.ciphertext,
  );
  return new TextDecoder().decode(plaintext);
}
